import { useEffect, useState } from "react";
import type { BioPod } from "@/features/pods/types";
import { podsRepository } from "@/features/pods/podsRepository";

interface PodsState {
  pods: BioPod[];
  isLoading: boolean;
  error: string | null;
}

/** Every pod this user can see. */
export function usePods(): PodsState {
  const [state, setState] = useState<PodsState>({ pods: [], isLoading: true, error: null });

  useEffect(() => {
    // Guards against both the unmount race and StrictMode's deliberate double-invoke: whichever
    // effect is torn down stops being able to write state.
    let active = true;

    podsRepository
      .listPods()
      .then((pods) => {
        if (active) setState({ pods, isLoading: false, error: null });
      })
      .catch((cause: unknown) => {
        if (active) {
          setState({
            pods: [],
            isLoading: false,
            error: cause instanceof Error ? cause.message : "Could not load pods.",
          });
        }
      });

    return () => {
      active = false;
    };
  }, []);

  return state;
}

interface PodState {
  pod: BioPod | null;
  isLoading: boolean;
  /** Distinct from `error`: the request succeeded and there is no such pod. */
  notFound: boolean;
  error: string | null;
}

/** One pod by id. `id` is optional because it arrives from the route and may be absent. */
export function usePod(id: string | undefined): PodState {
  const [state, setState] = useState<PodState>({
    pod: null,
    isLoading: true,
    notFound: false,
    error: null,
  });

  useEffect(() => {
    let active = true;

    if (id === undefined) {
      setState({ pod: null, isLoading: false, notFound: true, error: null });
      return;
    }

    setState({ pod: null, isLoading: true, notFound: false, error: null });

    podsRepository
      .getPod(id)
      .then((pod) => {
        if (active) {
          setState({ pod, isLoading: false, notFound: pod === null, error: null });
        }
      })
      .catch((cause: unknown) => {
        if (active) {
          setState({
            pod: null,
            isLoading: false,
            notFound: false,
            error: cause instanceof Error ? cause.message : "Could not load this pod.",
          });
        }
      });

    return () => {
      active = false;
    };
  }, [id]);

  return state;
}
